import React, { useState, useEffect, useMemo } from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
  TextField,
  Button,
  Box,
  FormControlLabel,
  Checkbox,
  Grid,
  IconButton,
  Autocomplete,
  Typography,
  Paper,
  Snackbar,
  Alert,
  InputAdornment,
  CircularProgress,
  Tooltip,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { Customer } from '../../types/customer';
import { Member } from '../../types/member';
import { buildMemberShortName } from '../../utils/memberDisplay';
import {
  ConstructionFormData,
  ConstructionExpense,
  ConstructionVendor,
  ConstructionRemark
} from '../../types/construction';
import {
  AddressSuggestion,
  RouteInfo,
  SiteNameSearchResult,
  RouteOption,
  AddressSearchSuggestion,
  APIKeywordSearchResult,
  NormalizedAddressRecord,
  AddressRouteRecord
} from '../../types/address';
import { AddressInput } from './AddressInput';
import { ConfirmDialog } from '../Common/ConfirmDialog';
import { constructionService } from '../../services/construction';
import { NumberTextField } from '../Common/NumberTextField';
import { RouteSelectionDialog } from './RouteSelectionDialog';
import DirectionsIcon from '@mui/icons-material/Directions';
import { AddressSuggestionDialog } from './AddressSuggestionDialog';




interface ConstructionFormProps {
  initialData: ConstructionFormData | null;
  onSubmit: (data: ConstructionFormData) => Promise<void>;
  onCancel: () => void;
  loading: boolean;
  customers: Customer[];
  members: Member[];
  selectedCustomer: Customer | null;
  selectedMember: Member | null;
  onCustomerChange: (customer: Customer | null) => void;
  onMemberChange: (member: Member | null) => void;
  onNewCustomerClick: () => void;
  setRouteInfo: (routeInfo: RouteInfo | null) => void;
  mode?: 'create' | 'edit'; // 追加
}

export const ConstructionForm: React.FC<ConstructionFormProps> = ({
  initialData,
  onSubmit,
  onCancel,
  loading,
  customers,
  members,
  selectedCustomer,
  selectedMember,
  onCustomerChange,
  onMemberChange,
  onNewCustomerClick,
  mode = 'create', // デフォルト値を設定
}) => {
  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    watch,
    register,
    formState: { errors }
  } = useForm<ConstructionFormData>({
    defaultValues: {
      quotationNo: initialData?.quotationNo || '',
      customerName: initialData?.customerName || '',
      supervisor: initialData?.supervisor || '',
      siteName: initialData?.siteName || '',
      address: initialData?.address || '',
      constructionDetails: initialData?.constructionDetails || '',
      startDate: initialData?.startDate || '',
      finishDate: initialData?.finishDate || '',
      estimatedAmount: initialData?.estimatedAmount || 0,
      isCompleted: Boolean(initialData?.isCompleted) || false,
      isBusinessTrip: Boolean(initialData?.isBusinessTrip) || false,
      billingMonth: initialData?.billingMonth || '',
      expenses: initialData?.expenses || [],
      vendors: initialData?.vendors || [],
      remarks: initialData?.remarks || []
    }
  });

  type ArrayFieldName = 'expenses' | 'vendors' | 'remarks';

  const [addressValue, setAddressValue] = useState(initialData?.address || '');
  const [addressSuggestion, setAddressSuggestion] = useState<AddressSuggestion | null>(null);
  // const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  // const [submissionData, setSubmissionData] = useState<ConstructionFormData | null>(null);
  const [routeInfo, setRouteInfo] = useState<RouteInfo | null>(null);
  const [selectedDistance, setSelectedDistance] = useState<number>(0);
  const [selectedTolls, setSelectedTolls] = useState({
    light: 0,
    standard: 0,
    medium: 0,
    large: 0
  });
  const [isLoading, setIsLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'error' as 'error' | 'success' | 'info' | 'warning'
  });
  const [dialogType, setDialogType] = useState<'supervisor' | null>(null);

  // メンバー情報をID順にソート
  const sortedMembers = useMemo(() =>
    [...members].sort((a, b) => (a.ID ?? 0) - (b.ID ?? 0)),
    [members]
  );
  const [routeDialogOpen, setRouteDialogOpen] = useState(false);
  const [routeOptions, setRouteOptions] = useState<any[]>([]);
  const [routeSearchLoading, setRouteSearchLoading] = useState(false);
  const [addressSuggestions, setAddressSuggestions] = useState<AddressSearchSuggestion[]>([]);
  const [addressDialogOpen, setAddressDialogOpen] = useState(false);
  const [addressSearchLoading, setAddressSearchLoading] = useState(false);
  const [addressState, setAddressState] = useState<NormalizedAddressRecord>({
    ID: null,
    Original_Address: initialData?.address || '',
    Normalized_Address: initialData?.address || '',
    Prefecture: '',
    City: '',
    Street: '',
    Building: '',
    Postal_Code: ''
  });

  const [routeState, setRouteState] = useState<AddressRouteRecord>({
    ID: null,
    Normalized_Address_ID: null,
    Distance: 0,
    Light_Vehicle_Toll: 0,
    Standard_Vehicle_Toll: 0,
    Medium_Vehicle_Toll: 0,
    Large_Vehicle_Toll: 0
  });

  useEffect(() => {
    if (initialData) {
      // 基本フィールドの設定
      Object.entries(initialData).forEach(([key, value]) => {
        // 配列フィールドは個別に処理
        if (!['expenses', 'vendors', 'remarks'].includes(key)) {
          setValue(key as keyof ConstructionFormData, value);
        }
      });

      // expenses の処理
      if (initialData.expenses) {
        const formattedExpenses = initialData.expenses.map(expense => ({
          price: expense.price,
          detail: expense.detail,
          constructionId: expense.ID || 0
        }));
        setValue('expenses', formattedExpenses);
      }

      // vendors の処理
      if (initialData.vendors) {
        const formattedVendors = initialData.vendors.map(vendor => ({
          price: vendor.price,
          detail: vendor.detail,
          constructionId: vendor.ID || 0
        }));
        setValue('vendors', formattedVendors);
      }

      // remarks の処理
      if (initialData.remarks) {
        const formattedRemarks = initialData.remarks.map(remark => ({
          remarks: remark.remarks,
          constructionId: remark.ID || 0
        }));
        setValue('remarks', formattedRemarks);
      }
    }
  }, [initialData, setValue]);



  useEffect(() => {
    if (initialData?.Route_Info_ID && initialData.address) {
      // 経路情報を取得
      const fetchRouteInfo = async () => {
        try {
          const result = await constructionService.getRouteInfo(initialData.address);
          // 既存の状態更新
          setRouteInfo(result);
          setValue('address', result.Normalized_Address);
          setAddressValue(result.Normalized_Address);
          setSelectedDistance(result.Distance);
          setSelectedTolls({
            light: result.Light_Vehicle_Toll,
            standard: result.Standard_Vehicle_Toll,
            medium: result.Medium_Vehicle_Toll,
            large: result.Large_Vehicle_Toll
          });

          // 新しい状態も更新
          setAddressState({
            ID: result.Normalized_Address_ID || null,
            Original_Address: initialData.address,
            Normalized_Address: result.Normalized_Address || initialData.address,
            Prefecture: result.Prefecture || '',
            City: result.City || '',
            Street: result.Street || '',
            Building: result.Building || '',
            Postal_Code: result.Postal_Code || ''
          });

          setRouteState({
            ID: result.ID || null,
            Normalized_Address_ID: result.Normalized_Address_ID || null,
            Distance: result.Distance || 0,
            Light_Vehicle_Toll: result.Light_Vehicle_Toll || 0,
            Standard_Vehicle_Toll: result.Standard_Vehicle_Toll || 0,
            Medium_Vehicle_Toll: result.Medium_Vehicle_Toll || 0,
            Large_Vehicle_Toll: result.Large_Vehicle_Toll || 0
          });
        } catch (error) {
          console.error('経路情報の取得に失敗:', error);
        }
      };
      fetchRouteInfo();
    }
  }, [initialData, setValue, setRouteInfo]);


  const handleArrayFieldChange = (
    index: number,
    field: string,
    value: string | number,
    arrayName: ArrayFieldName
  ) => {
    switch (arrayName) {
      case 'expenses': {
        const currentArray = getValues('expenses');
        const newArray = [...currentArray];
        newArray[index] = {
          ...newArray[index],
          // フィールド名を小文字に統一
          price: field === 'Price' || field === 'price' ? Number(value) : newArray[index].price,
          detail: field === 'Detail' || field === 'detail' ? String(value) : newArray[index].detail,
          constructionId: newArray[index]?.constructionId || 0
        };
        setValue('expenses', newArray);
        break;
      }
      case 'vendors': {
        const currentArray = getValues('vendors');
        const newArray = [...currentArray];
        newArray[index] = {
          ...newArray[index],
          // フィールド名を小文字に統一
          price: field === 'Price' || field === 'price' ? Number(value) : newArray[index].price,
          detail: field === 'Detail' || field === 'detail' ? String(value) : newArray[index].detail,
          constructionId: newArray[index]?.constructionId || 0
        };
        setValue('vendors', newArray);
        break;
      }
      case 'remarks': {
        const currentArray = getValues('remarks');
        const newArray = [...currentArray];
        newArray[index] = {
          ...newArray[index],
          // フィールド名を小文字に統一
          remarks: field === 'Remarks' || field === 'remarks' ? String(value) : newArray[index].remarks,
          constructionId: newArray[index]?.constructionId || 0
        };
        setValue('remarks', newArray);
        break;
      }
    }
  };






  // const handleSiteNameSearch = async () => {
  //   const siteName = getValues('siteName');
  //   if (!siteName) {
  //     setSnackbar({
  //       open: true,
  //       message: '現場名を入力してください',
  //       severity: 'warning'
  //     });
  //     return;
  //   }

  //   try {
  //     setAddressSearchLoading(true);

  //     // 常にHERE APIを使用して住所候補を取得
  //     const apiResults = await constructionService.searchAddressByKeyword(siteName);

  //     console.log("API レスポンス:", apiResults);

  //     if (apiResults && apiResults.length > 0) {
  //       // APIから取得した住所候補をフォーマット
  //       // suggestionパラメータに明示的に型APIKeywordSearchResultを指定
  //       const formattedSuggestions = apiResults.map((suggestion: APIKeywordSearchResult) => ({
  //         title: suggestion.title || "",
  //         address: suggestion.address || "",
  //         normalizedAddress: suggestion.normalizedAddress || suggestion.address || "",
  //         details: suggestion.details || {
  //           prefecture: "",
  //           city: "",
  //           district: "",
  //           street: "",
  //           houseNumber: "",
  //           postalCode: ""
  //         },
  //         location: suggestion.location || { lat: 0, lng: 0 }
  //       }));

  //       console.log("整形後の住所候補:", formattedSuggestions);

  //       setAddressSuggestions(formattedSuggestions);
  //       setAddressDialogOpen(true);
  //     } else {
  //       setSnackbar({
  //         open: true,
  //         message: '住所候補が見つかりませんでした',
  //         severity: 'info'
  //       });
  //     }
  //   } catch (error) {
  //     console.error('住所検索エラー:', error);
  //     setSnackbar({
  //       open: true,
  //       message: '住所候補の取得に失敗しました',
  //       severity: 'error'
  //     });
  //   } finally {
  //     setAddressSearchLoading(false);
  //   }
  // };


  const handleSiteNameSearch = async () => {
    const siteName = getValues('siteName');
    if (!siteName) {
      setSnackbar({
        open: true,
        message: '現場名を入力してください',
        severity: 'warning'
      });
      return;
    }

    try {
      setAddressSearchLoading(true);

      // APIを呼び出す（既存コード）
      const apiResults = await constructionService.searchAddressByKeyword(siteName);

      if (apiResults && apiResults.length > 0) {
        // 既存の処理を維持
        const formattedSuggestions = apiResults.map((suggestion) => ({
          title: suggestion.title || "",
          address: suggestion.address || "",
          normalizedAddress: suggestion.normalizedAddress || suggestion.address || "",
          details: suggestion.details || {
            prefecture: "",
            city: "",
            district: "",
            street: "",
            houseNumber: "",
            postalCode: ""
          },
          location: suggestion.location || { lat: 0, lng: 0 }
        }));

        setAddressSuggestions(formattedSuggestions);
        setAddressDialogOpen(true);
      } else {
        setSnackbar({
          open: true,
          message: '住所候補が見つかりませんでした',
          severity: 'info'
        });
      }
    } catch (error) {
      console.error('住所検索エラー:', error);
      setSnackbar({
        open: true,
        message: '住所候補の取得に失敗しました',
        severity: 'error'
      });
    } finally {
      setAddressSearchLoading(false);
    }
  };



  const handleSelectAddress = async (suggestion: AddressSearchSuggestion) => {
    setAddressDialogOpen(false);

    // 住所の加工と住所ステートの更新は現状のままで良い
    let cleanedAddress = suggestion.normalizedAddress || suggestion.title || "";

    // titleを削除
    if (suggestion.title && cleanedAddress.includes(suggestion.title)) {
      cleanedAddress = cleanedAddress.replace(suggestion.title, "").trim();
    }

    // 郵便番号を削除
    cleanedAddress = cleanedAddress.replace(/〒\d{3}-\d{4}\s*/, "").trim();

    // addressStateを更新
    const newAddressState = {
      ID: null,
      Original_Address: cleanedAddress,
      Normalized_Address: cleanedAddress,
      Prefecture: suggestion.details?.prefecture || "",
      City: suggestion.details?.city || "",
      Street: suggestion.details?.street || suggestion.details?.district || "",
      Building: suggestion.details?.houseNumber || "",
      Postal_Code: suggestion.details?.postalCode || ""
    };

    setAddressState(newAddressState);

    // 既存コードと同様に住所情報を保持
    const selectedAddressDetails = {
      originalAddress: cleanedAddress,
      normalizedAddress: cleanedAddress,
      details: suggestion.details || {
        prefecture: "",
        city: "",
        district: "",
        street: "",
        postalCode: ""
      }
    };

    // フォームに設定
    setValue('address', cleanedAddress);
    setAddressValue(cleanedAddress);

    try {
      // 選択された住所がDBに登録済みか確認
      const existingRouteInfo = await constructionService.checkExistingRouteInfo(cleanedAddress);

      if (existingRouteInfo) {
        // DBに登録済みの場合はその情報を使用
        setRouteInfo(existingRouteInfo);
        setSelectedDistance(existingRouteInfo.Distance);
        setSelectedTolls({
          light: existingRouteInfo.Light_Vehicle_Toll,
          standard: existingRouteInfo.Standard_Vehicle_Toll,
          medium: existingRouteInfo.Medium_Vehicle_Toll,
          large: existingRouteInfo.Large_Vehicle_Toll
        });

        // ここに追加: routeStateも更新
        setRouteState({
          ID: existingRouteInfo.ID || null,
          Normalized_Address_ID: existingRouteInfo.Normalized_Address_ID || null,
          Distance: existingRouteInfo.Distance || 0,
          Light_Vehicle_Toll: existingRouteInfo.Light_Vehicle_Toll || 0,
          Standard_Vehicle_Toll: existingRouteInfo.Standard_Vehicle_Toll || 0,
          Medium_Vehicle_Toll: existingRouteInfo.Medium_Vehicle_Toll || 0,
          Large_Vehicle_Toll: existingRouteInfo.Large_Vehicle_Toll || 0
        });


        // 住所ステートのIDも更新
        setAddressState(prev => ({
          ...prev,
          ID: existingRouteInfo.Normalized_Address_ID || null
        }));

        setSnackbar({
          open: true,
          message: '既存の経路情報を取得しました',
          severity: 'success'
        });
      } else {
        // DBに登録されていない場合は複数経路検索を行う
        setRouteSearchLoading(true);
        const routes = await constructionService.getMultipleRoutes(cleanedAddress);

        if (routes && routes.length > 0) {
          // 経路情報に住所詳細情報を付加
          const routesWithAddressDetails = routes.map((route: RouteOption) => ({
            ...route,
            // 住所詳細情報を追加
            addressDetails: selectedAddressDetails,
            // 新しい住所ステートも追加
            addressState: newAddressState
          }));

          setRouteOptions(routesWithAddressDetails);
          setRouteDialogOpen(true);
        } else {
          // 経路候補が見つからない場合は単一経路検索
          try {
            const routeInfo = await constructionService.getRouteInfo(cleanedAddress);
            if (routeInfo) {
              setRouteInfo(routeInfo);
              setSelectedDistance(routeInfo.Distance);
              setSelectedTolls({
                light: routeInfo.Light_Vehicle_Toll,
                standard: routeInfo.Standard_Vehicle_Toll,
                medium: routeInfo.Medium_Vehicle_Toll,
                large: routeInfo.Large_Vehicle_Toll
              });

              // ここに追加: 単一経路の場合もrouteStateを更新
              const newRouteState = {
                ID: null, // 保存後に設定される
                Normalized_Address_ID: null, // 保存後に設定される
                Distance: routeInfo.Distance || 0,
                Light_Vehicle_Toll: routeInfo.Light_Vehicle_Toll || 0,
                Standard_Vehicle_Toll: routeInfo.Standard_Vehicle_Toll || 0,
                Medium_Vehicle_Toll: routeInfo.Medium_Vehicle_Toll || 0,
                Large_Vehicle_Toll: routeInfo.Large_Vehicle_Toll || 0
              };

              setRouteState(newRouteState);

              // 単一経路の場合も保存
              const saveResult = await constructionService.saveSelectedRoute(
                selectedAddressDetails.originalAddress,
                selectedAddressDetails.normalizedAddress,
                {
                  distance: routeInfo.Distance,
                  lightVehicleToll: routeInfo.Light_Vehicle_Toll,
                  standardVehicleToll: routeInfo.Standard_Vehicle_Toll,
                  mediumVehicleToll: routeInfo.Medium_Vehicle_Toll,
                  largeVehicleToll: routeInfo.Large_Vehicle_Toll,
                  details: selectedAddressDetails.details
                }
              );

              // 保存が成功したら、IDを更新
              if (saveResult && saveResult.success) {
                setAddressState(prev => ({
                  ...prev,
                  ID: saveResult.addressId
                }));

                setRouteState(prev => ({
                  ...prev,
                  ID: saveResult.routeId,
                  Normalized_Address_ID: saveResult.addressId
                }));
              }

              setSnackbar({
                open: true,
                message: '経路情報を取得して保存しました',
                severity: 'success'
              });
            }
          } catch (error) {
            console.error('単一経路検索エラー:', error);
            setSnackbar({
              open: true,
              message: '経路情報の取得に失敗しました。住所は保存されました。',
              severity: 'warning'
            });
          }
        }
      }
    } catch (error) {
      console.error('経路検索エラー:', error);
      setSnackbar({
        open: true,
        message: '経路情報の取得に失敗しました。住所は保存されました。',
        severity: 'warning'
      });
    } finally {
      setRouteSearchLoading(false);
    }
  };

  const handleRouteSearch = async () => {
    const address = getValues('address');
    if (!address) return;

    try {
      setIsLoading(true);
      const result = await constructionService.getRouteInfo(address);

      setRouteInfo(result);

      setAddressValue(result.Normalized_Address);
      setValue('address', result.Normalized_Address);

      setSelectedDistance(result.Distance);
      setSelectedTolls({
        light: result.Light_Vehicle_Toll,
        standard: result.Standard_Vehicle_Toll,
        medium: result.Medium_Vehicle_Toll,
        large: result.Large_Vehicle_Toll
      });
    } catch (error) {
      console.error('経路情報取得エラー:', error);
      setSnackbar({
        open: true,
        message: '経路情報の取得に失敗しました',
        severity: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // 料金変更ハンドラ
  const handleTollChange = (type: 'light' | 'standard' | 'medium' | 'large', value: string | number) => {
    // 空文字チェック
    if (value === '') {
      setSelectedTolls(prev => ({
        ...prev,
        [type]: 0
      }));
      return;
    }

    // 数値に変換して検証
    const numValue = typeof value === 'string' ? Number(value) : value;

    // NaNチェック
    if (!isNaN(numValue)) {
      setSelectedTolls(prev => ({
        ...prev,
        [type]: numValue
      }));
    }
  };



  // 経路を選択したときの処理
  // const handleSelectRoute = async (route: RouteOption) => {
  //   try {
  //     setRouteDialogOpen(false);

  //     // フォームに選択した経路情報を設定
  //     setRouteInfo({
  //       ID: route.routeId || 0,
  //       Normalized_Address: addressValue,
  //       Distance: route.distance,
  //       Light_Vehicle_Toll: route.tollFee,
  //       Standard_Vehicle_Toll: route.tollFee,
  //       Medium_Vehicle_Toll: route.tollFee,
  //       Large_Vehicle_Toll: route.tollFee,
  //       distanceInKm: route.distanceInKm
  //     });

  //     setSelectedDistance(route.distance);
  //     setSelectedTolls({
  //       light: route.tollFee,
  //       standard: route.tollFee,
  //       medium: route.tollFee,
  //       large: route.tollFee
  //     });

  //     // 選択した経路情報を保存
  //     if (addressValue) {
  //       // 経路オプションに住所詳細があればそれを使用
  //       const addressDetails = route.addressDetails?.details || {};

  //       await constructionService.saveSelectedRoute(
  //         getValues('siteName'), // 元の検索キーワード
  //         addressValue, // 正規化された住所
  //         {
  //           distance: route.distance,
  //           lightVehicleToll: route.tollFee,
  //           standardVehicleToll: route.tollFee,
  //           mediumVehicleToll: route.tollFee,
  //           largeVehicleToll: route.tollFee,
  //           details: {
  //             prefecture: addressDetails.prefecture || "",
  //             city: addressDetails.city || "",
  //             street: addressDetails.street || addressDetails.district || "",
  //             postalCode: addressDetails.postalCode || "",
  //             building: ""
  //           }
  //         }
  //       );
  //     }

  //     setSnackbar({
  //       open: true,
  //       message: '経路情報を選択しました',
  //       severity: 'success'
  //     });
  //   } catch (error) {
  //     console.error('経路選択エラー:', error);
  //     setSnackbar({
  //       open: true,
  //       message: '経路情報の保存に失敗しました',
  //       severity: 'error'
  //     });
  //   }
  // };


  const handleSelectRoute = async (route: RouteOption) => {
    try {
      setRouteDialogOpen(false);

      // 普通車と中型車の料金情報を使用（APIから取得済み）
      const standardToll = route.Standard_Vehicle_Toll || route.tollFee;
      const mediumToll = route.Medium_Vehicle_Toll || route.tollFee;

      // フォームに選択した経路情報を設定（普通車と中型車の料金は正確な値）
      setRouteInfo({
        ID: route.routeId || 0,
        Normalized_Address: addressValue,
        Distance: route.distance,
        Standard_Vehicle_Toll: standardToll,
        Medium_Vehicle_Toll: mediumToll,
        // 軽自動車と大型車は仮に設定（後で更新）
        Light_Vehicle_Toll: 0,
        Large_Vehicle_Toll: 0,
        distanceInKm: route.distanceInKm
      });

      setSelectedDistance(route.distance);
      setSelectedTolls({
        standard: standardToll,
        medium: mediumToll,
        light: 0, // 一時的に0
        large: 0  // 一時的に0
      });

      // 新しいrouteStateを作成（普通車と中型車の料金のみ正確）
      const newRouteState = {
        ID: route.routeId || null,
        Normalized_Address_ID: null, // 保存後に設定される
        Distance: route.distance,
        Standard_Vehicle_Toll: standardToll,
        Medium_Vehicle_Toll: mediumToll,
        Light_Vehicle_Toll: 0,
        Large_Vehicle_Toll: 0
      };

      // 一時的なrouteStateを更新
      setRouteState(newRouteState);

      // 選択した経路情報を保存
      if (addressValue) {
        const currentAddressState = { ...addressState };
        const addressDetails = route.addressDetails?.details || {};

        try {
          const result = await constructionService.saveSelectedRoute(
            addressValue,
            addressValue,
            {
              distance: route.distance,
              standardVehicleToll: standardToll,
              mediumVehicleToll: mediumToll,
              lightVehicleToll: 0, // バックエンドでは使用しない
              largeVehicleToll: 0, // バックエンドでは使用しない
              details: {
                prefecture: currentAddressState.Prefecture || addressDetails.prefecture || "",
                city: currentAddressState.City || addressDetails.city || "",
                street: currentAddressState.Street || addressDetails.street || addressDetails.district || "",
                postalCode: currentAddressState.Postal_Code || addressDetails.postalCode || "",
                building: currentAddressState.Building || ""
              }
            }
          );

          // 保存が成功したら、IDを更新
          if (result && result.success) {
            // IDを更新
            setAddressState(prev => ({
              ...prev,
              ID: result.addressId
            }));

            setRouteState(prev => ({
              ...prev,
              ID: result.routeId,
              Normalized_Address_ID: result.addressId
            }));

            // routeInfoのIDも更新
            setRouteInfo(prev => {
              if (!prev) return prev;
              return {
                ...prev,
                ID: result.routeId,
                Normalized_Address_ID: result.addressId
              };
            });
          }

          // 保存が成功したら、別途全車種の料金を取得
          // if (result && result.success) {
          //   // IDを更新
          //   setAddressState(prev => ({
          //     ...prev,
          //     ID: result.addressId
          //   }));

          //   setRouteState(prev => ({
          //     ...prev,
          //     ID: result.routeId,
          //     Normalized_Address_ID: result.addressId
          //   }));

          //   // 別途全車種の料金を取得
          //   try {
          //     // routeCalculationServiceをconstructionServiceに変更してください（環境に応じて）
          //     const allVehicleTolls = await constructionService.getAllVehicleTypeTolls(addressValue, route.distance);

          //     if (allVehicleTolls) {
          //       // 全車種の料金でrouteStateを更新
          //       setRouteState(prev => ({
          //         ...prev,
          //         Light_Vehicle_Toll: allVehicleTolls.Light_Vehicle_Toll,
          //         Large_Vehicle_Toll: allVehicleTolls.Large_Vehicle_Toll
          //       }));

          //       // UI表示用のtoll情報も更新
          //       setSelectedTolls(prev => ({
          //         ...prev,
          //         light: allVehicleTolls.Light_Vehicle_Toll,
          //         large: allVehicleTolls.Large_Vehicle_Toll
          //       }));

          //       // 経路情報も更新
          //       setRouteInfo(prev => {
          //         if (!prev) return prev; // prevがnullの場合はそのまま返す

          //         return {
          //           ...prev,
          //           Light_Vehicle_Toll: allVehicleTolls.Light_Vehicle_Toll,
          //           Large_Vehicle_Toll: allVehicleTolls.Large_Vehicle_Toll
          //         };
          //       });

          //       console.log("全車種料金を更新:", allVehicleTolls);
          //     }
          //   } catch (tollError) {
          //     console.error('料金情報取得エラー:', tollError);
          //   }

          //   console.log("経路情報保存成功:", {
          //     addressId: result.addressId,
          //     routeId: result.routeId
          //   });
          // }
        } catch (error) {
          console.error('経路保存エラー:', error);
          // 料金情報の取得を試みる（上記と同じコード）
        }
      }

      setSnackbar({
        open: true,
        message: '経路情報を選択しました',
        severity: 'success'
      });
    } catch (error) {
      console.error('経路選択エラー:', error);
      setSnackbar({
        open: true,
        message: '経路情報の保存に失敗しました',
        severity: 'error'
      });
    }
  };


  // 住所から複数の経路を検索する関数
  const handleMultipleRoutesSearch = async () => {
    const address = getValues('address');
    if (!address) return;

    try {
      setRouteSearchLoading(true);
      const routes = await constructionService.getMultipleRoutes(address);

      if (routes && routes.length > 0) {
        // 正規化された住所を取得
        let normalizedAddress = routes[0].normalizedAddress;

        // 万が一郵便番号が含まれている場合は削除
        if (normalizedAddress) {
          normalizedAddress = normalizedAddress.replace(/〒\d{3}-\d{4}\s*/, "").trim();

          setAddressValue(normalizedAddress);
          setValue('address', normalizedAddress);

          // addressStateも更新
          setAddressState(prev => ({
            ...prev,
            Normalized_Address: normalizedAddress,
            Original_Address: routes[0].originalAddress || address
          }));
        }

        setRouteOptions(routes);
        setRouteDialogOpen(true);
      }
    } catch (error) {
      console.error('経路検索エラー:', error);
      setSnackbar({
        open: true,
        message: '経路候補の取得に失敗しました',
        severity: 'error'
      });
    } finally {
      setRouteSearchLoading(false);
    }
  };



  // addArrayFieldの修正
  const addArrayField = (arrayName: ArrayFieldName, initialItem?: any) => {
    switch (arrayName) {
      case 'expenses': {
        const currentArray = getValues('expenses');
        const newItem: ConstructionExpense = initialItem || {
          price: 0,
          detail: '',
          constructionId: 0
        };
        setValue('expenses', [...currentArray, newItem]);
        break;
      }
      case 'vendors': {
        const currentArray = getValues('vendors');
        const newItem: ConstructionVendor = initialItem || {
          price: 0,
          detail: '',
          constructionId: 0
        };
        setValue('vendors', [...currentArray, newItem]);
        break;
      }
      case 'remarks': {
        const currentArray = getValues('remarks');
        const newItem: ConstructionRemark = initialItem || {
          remarks: '',
          constructionId: 0
        };
        setValue('remarks', [...currentArray, newItem]);
        break;
      }
    }
  };

  // removeArrayFieldの修正
  const removeArrayField = (index: number, arrayName: ArrayFieldName) => {
    switch (arrayName) {
      case 'expenses': {
        const currentArray = getValues('expenses');
        setValue('expenses', currentArray.filter((_, i) => i !== index));
        break;
      }
      case 'vendors': {
        const currentArray = getValues('vendors');
        setValue('vendors', currentArray.filter((_, i) => i !== index));
        break;
      }
      case 'remarks': {
        const currentArray = getValues('remarks');
        setValue('remarks', currentArray.filter((_, i) => i !== index));
        break;
      }
    }
  };

  // 日付をYYYY-MM-DD形式に変換するヘルパー関数を追加
  const formatDateForInput = (dateString: string | undefined, format: 'date' | 'month' = 'date') => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (format === 'month') {
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    }
    return date.toISOString().split('T')[0];
  };

  const processSubmission = async (formData: ConstructionFormData): Promise<ConstructionFormData> => {
    // 新しい経路情報がある場合は、それを使用

    if (routeInfo) {
      const updatedRouteInfo: RouteInfo = {
        ...routeInfo,
        Normalized_Address: addressValue,
        Distance: selectedDistance,
        Light_Vehicle_Toll: selectedTolls.light,
        Standard_Vehicle_Toll: selectedTolls.standard,
        Medium_Vehicle_Toll: selectedTolls.medium,
        Large_Vehicle_Toll: selectedTolls.large
      };

      return {
        ...formData,
        Route_Info_ID: routeInfo.ID,
        routeInfo: updatedRouteInfo
      };
    }

    // 経路情報がない場合は新規取得を試みる
    if (!routeInfo && formData.address) {
      setIsLoading(true);
      try {
        const result = await constructionService.getRouteInfo(formData.address);
        setRouteInfo(result);
        return {
          ...formData,
          Route_Info_ID: result.ID,
          routeInfo: result
        };
      } catch (error) {
        console.error('経路情報取得エラー:', error);
        setSnackbar({
          open: true,
          message: '経路情報の取得に失敗しました',
          severity: 'error'
        });
        throw error;
      } finally {
        setIsLoading(false);
      }
    }

    // 経路情報がない場合
    return {
      ...formData,
      Route_Info_ID: null,
      routeInfo: null
    };
  };


  // function watch(arg0: string): unknown {
  //   throw new Error('Function not implemented.');
  // }

  return (
    <>
      <Paper elevation={3} sx={{ p: 3 }}>
        <form onSubmit={handleSubmit(async (data: ConstructionFormData) => {
          try {
            const processedData = await processSubmission(data);
            await onSubmit(processedData);
          } catch (error) {
            console.error('送信エラー:', error);
            throw error;
          }
        })}>
          <Grid container spacing={4}>
            {/* セクション1: 基本情報 */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{
                pb: 2,
                mb: 2,
                borderBottom: 2,
                borderColor: 'primary.main',  // プライマリーカラーを使用
                color: 'primary.main',
                fontWeight: 'medium'
              }}>
                基本情報
              </Typography>
              <Grid container spacing={3}>
                {/* 顧客情報 */}
                <Grid item xs={12} md={6}>
                  <Controller
                    name="customerName"
                    control={control}
                    rules={{ required: '顧客名は必須です' }}
                    render={({ field }) => (
                      <Autocomplete
                        value={selectedCustomer}
                        onChange={(_, newValue) => {
                          onCustomerChange(newValue);
                          field.onChange(newValue?.Customer_Name || '');
                        }}
                        options={customers}
                        getOptionLabel={(option) => option.Customer_Name}
                        isOptionEqualToValue={(option, value) =>
                          option.ID === value.ID || option.Customer_Name === value.Customer_Name
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={
                              <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
                                顧客名
                                <Box component="span" sx={{
                                  color: 'error.main',
                                  ml: 0.5,
                                  fontSize: '1.2rem'
                                }}>*</Box>
                              </Box>
                            }
                            error={!!errors.customerName}
                            helperText={errors.customerName?.message}
                          />
                        )}
                        noOptionsText="該当する顧客がいません"
                      />
                    )}
                  />
                </Grid>

                {/* 担当情報 */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={
                      <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
                        担当
                        <Box component="span" sx={{
                          color: 'error.main',
                          ml: 0.5,
                          fontSize: '1.2rem'
                        }}>*</Box>
                      </Box>
                    }
                    value={watch('supervisor')}
                    InputProps={{
                      readOnly: true,
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={() => setDialogType('supervisor')}
                          >
                            <PersonSearchIcon />
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                    onClick={() => setDialogType('supervisor')}
                    error={!!errors?.supervisor}
                    // helperText={errors?.supervisor?.message || '担当の選択は必須です'}
                    {...register('supervisor', { required: '担当を選択してください' })}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        bgcolor: 'background.paper',
                        cursor: 'pointer',
                        '&:hover': {
                          bgcolor: 'action.hover'
                        }
                      }
                    }}
                  />
                </Grid>

                {/* 担当選択用のダイアログ */}
                <Dialog
                  open={dialogType === 'supervisor'}
                  onClose={() => setDialogType(null)}
                  fullWidth
                  maxWidth="sm"
                  disableRestoreFocus
                >
                  <DialogTitle sx={{
                    borderBottom: 1,
                    borderColor: 'divider',
                    mb: 2
                  }}>
                    担当選択
                  </DialogTitle>
                  <DialogContent dividers>
                    <Grid container spacing={1}>
                      {sortedMembers.map((member) => {
                        const supervisorName = buildMemberShortName(member, members);
                        const isSelected = watch('supervisor') === supervisorName;
                        return (
                          <Grid item xs={4} key={`supervisor-${member.ID}`}>
                            <Button
                              fullWidth
                              variant={isSelected ? "contained" : "outlined"}
                              color={isSelected ? "success" : "primary"}
                              onClick={() => {
                                onMemberChange(member);
                                setValue('supervisor', supervisorName);
                                setDialogType(null);
                              }}
                              sx={{
                                height: '60px',
                                whiteSpace: 'normal',
                                lineHeight: 1.2,
                                textTransform: 'none',
                                ...(isSelected && {
                                  fontWeight: 'bold',
                                  boxShadow: 1,
                                }),
                              }}
                            >
                              <Box>
                                <Typography variant="body1">{supervisorName}</Typography>
                              </Box>
                            </Button>
                          </Grid>
                        );
                      })}
                    </Grid>
                  </DialogContent>
                  <DialogActions sx={{ borderTop: 1, borderColor: 'divider', px: 3, py: 2 }}>
                    <Button
                      onClick={() => setDialogType(null)}
                      variant="outlined"
                    >
                      閉じる
                    </Button>
                  </DialogActions>
                </Dialog>

                {/* 現場名 */}
                <Grid item xs={12}>
                  <Controller
                    name="siteName"
                    control={control}
                    rules={{ required: '現場名は必須です' }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label={
                          <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
                            現場名
                            <Box component="span" sx={{
                              color: 'error.main',
                              ml: 0.5,
                              fontSize: '1.2rem'
                            }}>*</Box>
                          </Box>
                        }
                        fullWidth
                        error={!!errors.siteName}
                        helperText={errors.siteName?.message}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-start', gap: 2 }}>
                    <Button
                      variant="outlined"
                      onClick={handleSiteNameSearch}
                      disabled={!watch('siteName') || addressSearchLoading}
                      startIcon={addressSearchLoading ? <CircularProgress size={20} /> : <SearchIcon />}
                    >
                      {addressSearchLoading ? '検索中...' : '現場名から住所を検索'}
                    </Button>
                  </Box>
                </Grid>

                {/* 工事内容 */}
                <Grid item xs={12}>
                  <Controller
                    name="constructionDetails"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="工事内容"
                        fullWidth
                        multiline
                        rows={1}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* セクション2: 場所情報 */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{
                pb: 2,
                mb: 2,
                borderBottom: 2,
                borderColor: 'primary.main',  // プライマリーカラーを使用
                color: 'primary.main',
                fontWeight: 'medium'
              }}>
                場所情報
              </Typography>

              <Grid container spacing={3}>
                {/* 住所入力 */}
                <Grid item xs={12}>
                  <Controller
                    name="address"
                    control={control}
                    render={({ field }) => (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <AddressInput
                          value={addressValue}
                          onChange={(address, suggestion) => {
                            field.onChange(address);
                            setAddressValue(address);
                            setAddressSuggestion(suggestion || null);

                            if (!address) {
                              setRouteInfo(null);
                              setSelectedDistance(0);
                              setSelectedTolls({
                                light: 0,
                                standard: 0,
                                medium: 0,
                                large: 0
                              });
                              return;
                            }

                            if (suggestion?.routeId && suggestion?.routeInfo) {
                              const routeInfoWithCorrectId = {
                                ...suggestion.routeInfo,
                                ID: suggestion.routeId
                              };

                              setRouteInfo(routeInfoWithCorrectId);
                              setSelectedDistance(routeInfoWithCorrectId.Distance);
                              setSelectedTolls({
                                light: routeInfoWithCorrectId.Light_Vehicle_Toll,
                                standard: routeInfoWithCorrectId.Standard_Vehicle_Toll,
                                medium: routeInfoWithCorrectId.Medium_Vehicle_Toll,
                                large: routeInfoWithCorrectId.Large_Vehicle_Toll
                              });
                            }
                          }}
                          error={!!errors.address}
                          helperText={errors.address?.message}
                        />

                        {/* <Button
                          variant="outlined"
                          onClick={handleRouteSearch}
                          disabled={!field.value || isLoading}
                          startIcon={isLoading ? <CircularProgress size={20} /> : <SearchIcon />}
                          sx={{ alignSelf: 'flex-start' }}
                        >
                          {isLoading ? '検索中...' : '経路情報を検索'}
                        </Button> */}
                        <Box sx={{ display: 'flex', gap: 2 }}>
                          <Button
                            variant="outlined"
                            onClick={handleRouteSearch}
                            disabled={!field.value || isLoading}
                            startIcon={isLoading ? <CircularProgress size={20} /> : <SearchIcon />}
                          >
                            {isLoading ? '検索中...' : '経路情報を検索'}
                          </Button>

                          <Button
                            variant="outlined"
                            onClick={handleMultipleRoutesSearch}
                            disabled={!field.value || routeSearchLoading}
                            startIcon={routeSearchLoading ? <CircularProgress size={20} /> : <DirectionsIcon />}
                            color="secondary"
                          >
                            {routeSearchLoading ? '検索中...' : '複数経路を検索'}
                          </Button>
                        </Box>
                      </Box>
                    )}
                  />
                </Grid>

                {/* 経路情報表示エリア */}
                {routeInfo && (
                  <Grid item xs={12}>
                    <Paper sx={{
                      p: 3,
                      bgcolor: 'background.default',
                      border: 1,
                      borderColor: 'divider'
                    }}>
                      <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'medium' }}>
                        経路情報（※距離、金額は要確認）
                      </Typography>

                      <Grid container spacing={3}>
                        {/* 走行距離の選択 */}
                        <Grid item xs={12}>
                          <NumberTextField
                            fullWidth
                            label="走行距離 (km)"
                            value={selectedDistance === 0 ? '' : Math.round((selectedDistance / 1000) * 10) / 10}
                            onChange={(e) => {
                              // 数値変換する前に空文字列チェック
                              const value = e.target.value;
                              if (value === '') {
                                setSelectedDistance(0);
                              } else {
                                const numericValue = Number(value);
                                // NaNチェックを追加
                                if (!isNaN(numericValue)) {
                                  setSelectedDistance(numericValue * 1000);
                                }
                              }
                            }}
                            helperText={routeInfo?.distanceInKm ? `データベース登録値: ${routeInfo.distanceInKm}km` : undefined}
                            InputProps={{
                              endAdornment: <InputAdornment position="end">km</InputAdornment>
                            }}
                          />

                        </Grid>

                        {/* 料金の選択（車種別） */}
                        {/* <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="軽自動車料金"
                            type="number"
                            value={selectedTolls.light}
                            onChange={(e) => handleTollChange('light', Number(e.target.value))}
                            helperText={`データベース登録値: ${routeInfo.Light_Vehicle_Toll}円`}
                            InputProps={{
                              endAdornment: <InputAdornment position="end">円</InputAdornment>
                            }}
                          />
                        </Grid> */}
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <NumberTextField
                          fullWidth
                          label="普通車料金"
                          value={selectedTolls.standard || ''}
                          onChange={(e) => handleTollChange('standard', e.target.value)}
                          helperText={`データベース登録値: ${routeInfo.Standard_Vehicle_Toll}円`}
                          InputProps={{
                            endAdornment: <InputAdornment position="end">円</InputAdornment>
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <NumberTextField
                          fullWidth
                          label="中型車料金"
                          value={selectedTolls.medium || ''}
                          onChange={(e) => handleTollChange('medium', e.target.value)}
                          helperText={`データベース登録値: ${routeInfo.Medium_Vehicle_Toll}円`}
                          InputProps={{
                            endAdornment: <InputAdornment position="end">円</InputAdornment>
                          }}
                        />

                      </Grid>
                      {/* <Grid item xs={12} sm={6}>
                        <NumberTextField
                          fullWidth
                          label="大型車料金"
                          value={selectedTolls.large || ''}
                          onChange={(e) => handleTollChange('large', e.target.value)}
                          helperText={`データベース登録値: ${routeInfo.Large_Vehicle_Toll}円`}
                          InputProps={{
                            endAdornment: <InputAdornment position="end">円</InputAdornment>
                          }}
                        />
                      </Grid> */}
                    </Paper>
                  </Grid>
                )}
              </Grid>
            </Grid>

            {/* セクション3: 工期・金額 */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{
                pb: 2,
                mb: 2,
                borderBottom: 2,
                borderColor: 'primary.main',  // プライマリーカラーを使用
                color: 'primary.main',
                fontWeight: 'medium'
              }}>
                工期・金額
              </Typography>

              <Grid container spacing={3}>
                {/* 開始日 */}
                <Grid item xs={12} md={6}>
                  <Controller
                    name="startDate"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        type="date"
                        value={formatDateForInput(field.value) || ''}
                        fullWidth
                        label="開始日"
                        InputLabelProps={{ shrink: true }}
                        sx={{
                          '& input': {
                            py: { xs: 1.5, md: 2 }
                          }
                        }}
                      />
                    )}
                  />
                </Grid>

                {/* 完了予定日 */}
                <Grid item xs={12} md={6}>
                  <Controller
                    name="finishDate"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        type="date"
                        value={formatDateForInput(field.value) || ''}
                        fullWidth
                        label="完了予定日"
                        InputLabelProps={{ shrink: true }}
                        sx={{
                          '& input': {
                            py: { xs: 1.5, md: 2 }
                          }
                        }}
                      />
                    )}
                  />
                </Grid>

                {/* 見積金額 */}
                <Grid item xs={12} md={6}>
                  <Controller
                    name="estimatedAmount"
                    control={control}
                    defaultValue={0}
                    render={({ field }) => (
                      <Tooltip title="見積金額は税抜きで入力してください" placement="top">
                        <NumberTextField
                          {...field}
                          value={field.value || ''}
                          label="見積金額"
                          fullWidth
                          thousandSeparator={true} // カンマ区切り表示を有効化
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value === '') {
                              field.onChange(0);
                            } else {
                              const numValue = Number(value);
                              if (!isNaN(numValue)) {
                                field.onChange(numValue);
                              }
                            }
                          }}
                          InputProps={{
                            endAdornment: <InputAdornment position="end">円</InputAdornment>,
                            startAdornment: (
                              <InputAdornment position="start">
                                <HelpOutlineIcon color="action" />
                              </InputAdornment>
                            )
                          }}
                        />
                      </Tooltip>
                    )}
                  />
                </Grid>

                {/* 請求月 */}
                <Grid item xs={12} md={6}>
                  <Controller
                    name="billingMonth"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        type="month"
                        value={formatDateForInput(field.value, 'month') || ''}
                        fullWidth
                        label="請求月"
                        InputLabelProps={{ shrink: true }}
                        sx={{
                          '& input': {
                            py: { xs: 1.5, md: 2 }
                          }
                        }}
                      />
                    )}
                  />
                </Grid>

                {/* 出張工事チェックボックス */}
                <Grid item xs={12}>
                  <Controller
                    name="isBusinessTrip"
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        control={
                          <Checkbox
                            {...field}
                            checked={Boolean(field.value)}
                            sx={{ '& .MuiSvgIcon-root': { fontSize: 28 } }}
                          />
                        }
                        label="出張工事"
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* セクション4: 経費 */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{
                pb: 2,
                mb: 2,
                borderBottom: 2,
                borderColor: 'primary.main',  // プライマリーカラーを使用
                color: 'primary.main',
                fontWeight: 'medium'
              }}>
                経費
              </Typography>

              {/* 経費セクション */}
              <Controller
                name="expenses"
                control={control}
                render={({ field }) => (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {field.value.length > 0 && field.value.map((expense, index) => (
                      <Paper
                        key={index}
                        sx={{
                          p: 2,
                          bgcolor: 'background.default',
                          border: 1,
                          borderColor: 'divider'
                        }}
                      >
                        <Box sx={{
                          display: 'flex',
                          flexDirection: { xs: 'column', sm: 'row' },
                          gap: 2
                        }}>
                          <NumberTextField
                            label="金額"
                            value={expense.price || ''}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value === '') {
                                handleArrayFieldChange(index, 'Price', 0, 'expenses');
                              } else {
                                const numValue = Number(value);
                                if (!isNaN(numValue)) {
                                  handleArrayFieldChange(index, 'Price', numValue, 'expenses');
                                }
                              }
                            }}
                            thousandSeparator={true}
                            sx={{
                              width: { xs: '100%', sm: '200px' }
                            }}
                          />
                          <TextField
                            fullWidth
                            label="詳細"
                            value={expense.detail}
                            onChange={(e) => handleArrayFieldChange(index, 'Detail', e.target.value, 'expenses')}
                            multiline
                            rows={1}  // 行数を明示的に指定
                            sx={{ flex: 1 }}
                          />
                          <IconButton
                            onClick={() => removeArrayField(index, 'expenses')}
                            color="error"
                            sx={{
                              alignSelf: { xs: 'flex-end', sm: 'flex-start' },
                              mt: 1
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      </Paper>
                    ))}
                    <Button
                      startIcon={<AddIcon />}
                      onClick={() => addArrayField('expenses')}
                      variant="outlined"
                      sx={{ alignSelf: 'flex-start' }}
                    >
                      経費を追加
                    </Button>
                  </Box>
                )}
              />
            </Grid>

            {/* セクション5: 外注費 */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{
                pb: 2,
                mb: 2,
                borderBottom: 2,
                borderColor: 'primary.main',  // プライマリーカラーを使用
                color: 'primary.main',
                fontWeight: 'medium'
              }}>
                外注費
              </Typography>

              {/* 外注費セクション */}
              <Controller
                name="vendors"
                control={control}
                render={({ field }) => (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {field.value.length > 0 && field.value.map((vendor, index) => (
                      <Paper
                        key={index}
                        sx={{
                          p: 2,
                          bgcolor: 'background.default',
                          border: 1,
                          borderColor: 'divider'
                        }}
                      >
                        <Box sx={{
                          display: 'flex',
                          flexDirection: { xs: 'column', sm: 'row' },
                          gap: 2
                        }}>
                          <NumberTextField
                            label="金額"
                            value={vendor.price || ''}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value === '') {
                                handleArrayFieldChange(index, 'Price', 0, 'vendors');
                              } else {
                                const numValue = Number(value);
                                if (!isNaN(numValue)) {
                                  handleArrayFieldChange(index, 'Price', numValue, 'vendors');
                                }
                              }
                            }}
                            thousandSeparator={true}
                            sx={{
                              width: { xs: '100%', sm: '200px' }
                            }}
                          />
                          <TextField
                            fullWidth
                            label="詳細"
                            value={vendor.detail}
                            onChange={(e) => handleArrayFieldChange(index, 'Detail', e.target.value, 'vendors')}
                            multiline
                            rows={1}
                            sx={{ flex: 1 }}
                          />
                          <IconButton
                            onClick={() => removeArrayField(index, 'vendors')}
                            color="error"
                            sx={{
                              alignSelf: { xs: 'flex-end', sm: 'flex-start' },
                              mt: 1
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      </Paper>
                    ))}
                    <Button
                      startIcon={<AddIcon />}
                      onClick={() => addArrayField('vendors')}
                      variant="outlined"
                      sx={{ alignSelf: 'flex-start' }}
                    >
                      外注費を追加
                    </Button>
                  </Box>
                )}
              />
            </Grid>

            {/* セクション6: 備考 */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{
                pb: 2,
                mb: 2,
                borderBottom: 2,
                borderColor: 'primary.main',  // プライマリーカラーを使用
                color: 'primary.main',
                fontWeight: 'medium'
              }}>
                備考
              </Typography>

              <Controller
                name="remarks"
                control={control}
                render={({ field }) => (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {field.value.length > 0 && field.value.map((remark, index) => (
                      <Paper
                        key={index}
                        sx={{
                          p: 2,
                          bgcolor: 'background.default',
                          border: 1,
                          borderColor: 'divider'
                        }}
                      >
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                          <TextField
                            fullWidth
                            label="備考"
                            value={remark.remarks}
                            onChange={(e) => handleArrayFieldChange(index, 'Remarks', e.target.value, 'remarks')}
                            multiline
                            rows={1}
                          />
                          <IconButton
                            onClick={() => removeArrayField(index, 'remarks')}
                            color="error"
                            sx={{ mt: 1 }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      </Paper>
                    ))}
                    <Button
                      startIcon={<AddIcon />}
                      onClick={() => addArrayField('remarks')}
                      variant="outlined"
                      sx={{ alignSelf: 'flex-start' }}
                    >
                      備考を追加
                    </Button>
                  </Box>
                )}
              />
            </Grid>

            {/* 送信ボタン */}
            <Grid item xs={12}>
              <Box sx={{
                display: 'flex',
                gap: 2,
                justifyContent: 'center',
                mt: 4
              }}>
                <Button
                  variant="outlined"
                  onClick={onCancel}
                  size="large"
                  sx={{ minWidth: 120 }}
                >
                  キャンセル
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={loading}
                  size="large"
                  sx={{ minWidth: 120 }}
                >
                  {loading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    mode === 'edit' ? '更新' : '登録'
                  )}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>

      <AddressSuggestionDialog
        open={addressDialogOpen}
        onClose={() => setAddressDialogOpen(false)}
        suggestions={addressSuggestions}
        loading={addressSearchLoading}
        onSelectAddress={handleSelectAddress}
        keyword={getValues('siteName')}
      />
      <RouteSelectionDialog
        open={routeDialogOpen}
        onClose={() => setRouteDialogOpen(false)}
        routes={routeOptions}
        loading={routeSearchLoading}
        onSelectRoute={handleSelectRoute}
        address={addressValue}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ConstructionForm;